import React, { useState, useEffect, useCallback } from 'react';
import api from './api';
import KPICard from './components/KPICard';
import SimpleBarChart from './components/BarChart';
import SimplePieChart from './components/PieChart';
import CustomLineChart from './components/LineChart';
import HorizontalBarChart from './components/HorizontalBarChart';
import FilterBar from './components/FilterBar';
import ExportButtons from './components/ExportButtons';
import SummaryCard from './components/SummaryCard';
import RequirementAnalysis from './components/RequirementAnalysis';
import SurveyDataTable from './components/SurveyDataTable'; 
import './App.css';

function App() {
    const [stats, setStats] = useState(null);
    const [trends, setTrends] = useState(null);
    const [demographics, setDemographics] = useState(null);
    const [infrastructure, setInfrastructure] = useState(null);
    const [problems, setProblems] = useState([]);
    const [recommendations, setRecommendations] = useState(null);
    const [geography, setGeography] = useState(null);
    const [servicePrefs, setServicePrefs] = useState(null);
    const [communes, setCommunes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [token, setToken] = useState('');
    const [dateRange, setDateRange] = useState('30');
    const [communeFilter, setCommuneFilter] = useState('all');
    const [lastRefresh, setLastRefresh] = useState(new Date());
    const [activeTab, setActiveTab] = useState('analytics');

    useEffect(() => {
        const savedToken = localStorage.getItem('dashboardToken');
        if (savedToken) {
            setToken(savedToken);
        }
    }, []);

    useEffect(() => {
        if (!token) return;
        const interval = setInterval(() => refreshData(), 30000);
        return () => clearInterval(interval);
    }, [token, dateRange, communeFilter]);

    const fetchAllData = async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const params = new URLSearchParams();
            if (dateRange !== 'all') params.append('days', dateRange);
            if (communeFilter !== 'all') params.append('commune', communeFilter);

            const [
                statsRes, trendsRes, demographicsRes, infrastructureRes,
                problemsRes, recommendationsRes, geographyRes
            ] = await Promise.all([
                api.get(`/dashboard/stats?${params}`),                 // add params
                api.get(`/dashboard/trends?${params}`),
                api.get(`/dashboard/demographics?${params}`),          // add params
                api.get(`/dashboard/infrastructure?${params}`),        // add params
                api.get(`/dashboard/problems?${params}`),              // add params
                api.get(`/dashboard/recommendations?${params}`),       // add params
                api.get(`/dashboard/geography?${params}`)              // add params
            ]);

            let servicePrefsData = {
                payment_methods: [],
                notification_preferences: [],
                preferred_times: []
            };
            try {
                const servicePrefsRes = await api.get('/dashboard/service-preferences');
                servicePrefsData = servicePrefsRes?.data?.data || servicePrefsData;
            } catch (e) {
                if (e?.response?.status !== 404) console.error(e);
            }

            setStats(statsRes.data.data);
            setTrends(trendsRes.data.data);
            setDemographics(demographicsRes.data.data);
            setInfrastructure(infrastructureRes.data.data);
            setProblems(problemsRes.data.data);
            setRecommendations(recommendationsRes.data.data);
            setGeography(geographyRes.data.data);
            setServicePrefs(servicePrefsData);

            if (geographyRes.data.data?.by_commune) {
                setCommunes(geographyRes.data.data.by_commune);
            }
            setLastRefresh(new Date());
            setError(null);
        } catch (err) {
            console.error(err);
            if (!silent) setError('Failed to load dashboard data. Please refresh.');
        } finally {
            if (!silent) setLoading(false);
        }
    };

    const refreshData = useCallback(async () => {
        if (!token || refreshing) return;
        setRefreshing(true);
        await fetchAllData(true);
        setRefreshing(false);
    }, [token, dateRange, communeFilter]);

    const handleLogin = async (username) => {
        try {
            const response = await api.post('/auth/login', { username });
            if (response.data.success && response.data.token) {
                const newToken = response.data.token;
                setToken(newToken);
                localStorage.setItem('dashboardToken', newToken);
                await fetchAllData();
            }
        } catch (err) {
            setError('Login failed. Please try again.');
        }
    };

    useEffect(() => {
        if (token) fetchAllData();
    }, [token, dateRange, communeFilter]);

    if (!token) {
        return (
            <div className="login-container">
                <div className="login-card">
                    <h1>📊 Survey Dashboard</h1>
                    <p>Enter your username to access analytics</p>
                    <input type="text" placeholder="Username" onKeyPress={(e) => e.key === 'Enter' && handleLogin(e.target.value)} id="username-input" />
                    <button onClick={() => { const input = document.getElementById('username-input'); handleLogin(input.value); }}>Login</button>
                </div>
            </div>
        );
    }

    if (loading) return <div className="loading">Loading dashboard data...</div>;
    if (error) return <div className="error">{error}</div>;

    return (
        <div className="dashboard">
            <header className="dashboard-header">
                <div>
                    <h1>📊 Survey Dashboard</h1>
                    <p className="last-refresh">Last updated: {lastRefresh.toLocaleTimeString()}</p>
                </div>
                <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                    {activeTab === 'analytics' && <ExportButtons token={token} filters={{ dateRange, commune: communeFilter }} />}
                    <button className="refresh-btn" onClick={refreshData} disabled={refreshing}>{refreshing ? '⟳ Refreshing...' : '🔄 Refresh'}</button>
                    <button className="logout-btn" onClick={() => { setToken(''); localStorage.removeItem('dashboardToken'); }}>Logout</button>
                </div>
            </header>

            <div className="dashboard-tabs">
                <button className={activeTab === 'analytics' ? 'tab-active' : 'tab-inactive'} onClick={() => setActiveTab('analytics')}>📈 Survey Analytics</button>
                <button className={activeTab === 'requirements' ? 'tab-active' : 'tab-inactive'} onClick={() => setActiveTab('requirements')}>🎯 Requirement Insights</button>
                <button className={activeTab === 'data' ? 'tab-active' : 'tab-inactive'} onClick={() => setActiveTab('data')}>📋 Survey Data</button>
            </div>

            {activeTab === 'analytics' && (
                <>
                    <FilterBar dateRange={dateRange} setDateRange={setDateRange} communeFilter={communeFilter} setCommuneFilter={setCommuneFilter} communes={communes} />
                    <div className="kpi-row">
                        <KPICard title="Total Surveys" value={stats?.total_surveys || 0} icon="📋" color="#3498db" />
                        <KPICard title="Sync Success Rate" value={`${trends?.sync_success_rate || 0}%`} icon="☁️" color="#2ecc71" />
                        <KPICard title="Communes" value={stats?.unique_communes || 0} icon="📍" color="#e74c3c" />
                        <KPICard title="Sections" value={stats?.unique_sections || 0} icon="🏘️" color="#f39c12" />
                        <KPICard title="Active Surveyors" value={stats?.active_surveyors || 0} icon="👥" color="#9b59b6" />
                    </div>

                    <div className="chart-row">
                        <CustomLineChart data={trends?.daily_counts || []} title="Surveys Over Time" dataKeys={['count']} colors={['#3498db']} />
                        <HorizontalBarChart data={problems.slice(0, 8)} title="Top 8 Problems (Ranked)" dataKey="count" nameKey="biggest_problem" />
                    </div>

                    <div className="section-title"><h2>👥 Respondent Demographics</h2></div>
                    <div className="chart-row">
                        <SimplePieChart data={demographics?.age_groups?.map(g => ({ name: g.age, value: parseInt(g.count, 10) })) || []} title="Age Distribution" />
                        <SimplePieChart data={demographics?.gender?.map(g => ({ name: g.gender, value: parseInt(g.count, 10) })) || []} title="Gender Ratio" />
                        <SimplePieChart data={demographics?.literacy?.map(g => ({ name: g.literacy, value: parseInt(g.count, 10) })) || []} title="Literacy Rate" />
                        <SimplePieChart data={demographics?.phone_ownership?.map(g => ({ name: g.phone_ownership, value: parseInt(g.count, 10) })) || []} title="Phone Ownership" />
                    </div>

                    <div className="section-title"><h2>⚡ Infrastructure & Access</h2></div>
                    <div className="chart-row">
                        <SummaryCard title="Electricity Access" data={infrastructure?.electricity || []} color="#f39c12" />
                        <SummaryCard title="Phone Signal Coverage" data={infrastructure?.phone_signal || []} color="#e74c3c" />
                        <SummaryCard title="Travel Time to Services" data={infrastructure?.travel_time || []} color="#3498db" />
                        <SummaryCard title="Popular Access Points" data={infrastructure?.accessible_locations || []} color="#2ecc71" />
                    </div>

                    <div className="section-title"><h2>💳 Service Preferences</h2></div>
                    <div className="chart-row">
                        <SimplePieChart data={servicePrefs?.payment_methods?.map(g => ({ name: g.payment_method, value: parseInt(g.count, 10) })) || []} title="Preferred Payment Method" />
                        <SimplePieChart data={servicePrefs?.notification_preferences?.map(g => ({ name: g.notification_preference, value: parseInt(g.count, 10) })) || []} title="Notification Preference" />
                        <SimpleBarChart data={servicePrefs?.preferred_times || []} title="Preferred Service Hours" dataKey="count" nameKey="preferred_time" />
                    </div>

                    <div className="section-title"><h2>🗺️ Geographic Distribution</h2></div>
                    <div className="chart-row">
                        <SimpleBarChart data={geography?.by_commune || []} title="Surveys by Commune" dataKey="count" nameKey="commune" />
                        <HorizontalBarChart data={(geography?.by_section || []).slice(0, 12)} title="Top 12 Sections" dataKey="count" nameKey="section" />
                    </div>

                    <div className="two-columns">
                        <div className="column">
                            <div className="card"><h3>💡 Community Suggestions</h3><ul className="recommendation-list">{recommendations?.suggestions?.slice(0, 15).map((sug, idx) => (<li key={idx}><span className="suggestion-text">"{sug.suggested_change}"</span><span className="suggestion-count">{sug.count} mentions</span></li>))}</ul></div>
                        </div>
                        <div className="column">
                            <div className="card"><h3>📍 Recommended Service Locations</h3><ul className="recommendation-list">{recommendations?.locations?.slice(0, 15).map((loc, idx) => (<li key={idx}><span className="loc-name">{loc.recommended_location}</span><span className="loc-count">{loc.count} recommendations</span></li>))}</ul></div>
                        </div>
                    </div>
                </>
            )}

            {activeTab === 'requirements' && <RequirementAnalysis token={token} />}

            {activeTab === 'data' && <SurveyDataTable communes={communes} />}
        </div>
    );
}

export default App;